import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Camera, Flashlight, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';

export const ScannerCamera = ({ mode, onCode, onPhoto, disabled }) => {
  const video = useRef(null), controls = useRef(null), locked = useRef(false), current = useRef({ onCode, onPhoto, disabled });
  current.current = { onCode, onPhoto, disabled };
  const [error, setError] = useState(''), [ready, setReady] = useState(true), [torch, setTorch] = useState(false), [canTorch, setCanTorch] = useState(false);
  useEffect(() => {
    let cancelled = false, stream;
    setError(''); locked.current = false; setReady(true);
    const start = async () => {
      try {
        if (mode === 'barcode') {
          const reader = new BrowserMultiFormatReader();
          const c = await reader.decodeFromConstraints({ video: { facingMode: { ideal: 'environment' } } }, video.current,
            result => {
              if (!result || locked.current || current.current.disabled) return;
              locked.current = true; setReady(false); current.current.onCode(result.getText());
            });
          if (cancelled) c.stop(); else controls.current = c;
        } else {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
          if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
          video.current.srcObject = stream; await video.current.play();
        }
        const track = video.current?.srcObject?.getVideoTracks()[0];
        if (!cancelled) setCanTorch(!!track?.getCapabilities?.().torch);
      } catch { if (!cancelled) setError('Camera unavailable. Allow access, enter a code or choose a photograph.'); }
    };
    start();
    return () => { cancelled = true; controls.current?.stop(); stream?.getTracks().forEach(t => t.stop()); };
  }, [mode]);
  const photograph = () => {
    const v = video.current;
    if (!v?.videoWidth) return;
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, 2000 / Math.max(v.videoWidth, v.videoHeight));
    canvas.width = Math.round(v.videoWidth*scale); canvas.height = Math.round(v.videoHeight*scale);
    canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => blob && current.current.onPhoto(blob), 'image/jpeg', .88);
  };
  const toggleTorch = async () => {
    try { await video.current.srcObject.getVideoTracks()[0].applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); }
    catch { setError('Torch is unavailable on this camera'); }
  };
  return <section className="scan-camera" data-testid="phone-camera">
    <video ref={video} muted playsInline autoPlay data-testid="phone-camera-video" />
    {error && <p className="scan-camera-error" role="alert" data-testid="phone-camera-error">{error}</p>}
    <div className="scan-camera-controls">
      {mode === 'photo' ? <Button disabled={disabled || !!error} onClick={photograph} data-testid="phone-capture-photo"><Camera size={18} /> Capture</Button>
        : <Button disabled={ready || disabled} onClick={() => { locked.current = false; setReady(true); }} data-testid="phone-next-code"><RotateCcw size={16} /> {ready ? 'Camera ready' : 'Next barcode'}</Button>}
      {canTorch && <Button variant="outline" onClick={toggleTorch} aria-label="Toggle torch" data-testid="phone-torch"><Flashlight size={18} /></Button>}
    </div>
  </section>;
};